export type LoginKind = "user" | "admin";

export type SendLoginOtpInput = {
  email: string;
  password: string;
};

export type SendLoginOtpResult = {
  channel: string;
  email?: string;
  message: string;
};

export type VerifyLoginOtpInput = {
  email: string;
  otp: string;
};

type ApiSuccess<T> = {
  success: boolean;
  message?: string;
  data?: T;
};

type VerifyLoginApiResponse = {
  success: boolean;
  message?: string;
  user?: unknown;
};

function getApiBase(kind: LoginKind) {
  return kind === "admin" ? "/api/auth/admin" : "/api/auth/user";
}

async function safeParseJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    return {} as T;
  }
}

export async function sendLoginOtp(
  kind: LoginKind,
  input: SendLoginOtpInput
): Promise<SendLoginOtpResult> {
  const response = await fetch(`${getApiBase(kind)}/send-otp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(input),
  });

  const json = await safeParseJson<ApiSuccess<{ channel: string; email?: string }>>(response);

  if (!response.ok || !json.success) {
    throw new Error(json.message || "Failed to send OTP. Please check your credentials or network connection.");
  }

  return {
    channel: json.data?.channel || "email",
    email: json.data?.email,
    message: json.message || "OTP sent successfully",
  };
}

export async function verifyLoginOtp(kind: LoginKind, input: VerifyLoginOtpInput) {
  const response = await fetch(`${getApiBase(kind)}/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(input),
  });

  const json = await safeParseJson<VerifyLoginApiResponse>(response);

  if (!response.ok || !json.success) {
    throw new Error(json.message || "OTP verification failed");
  }

  return json.user;
}

export async function loginUser(input: SendLoginOtpInput) {
  const response = await fetch("/api/auth/user/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(input),
  });

  const json = await safeParseJson<any>(response);

  if (!response.ok || !json.success) {
    throw new Error(json.message || "Login failed");
  }

  return json.user;
}
